import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { BookmarkCollection } from '../../api/types';
import { Dropdown } from '../Dropdown';
import * as S from './CollectionActionsDropdown.styled';
import { Portal } from '@/components/UI/Portal/Portal';
import { ConfirmDeleteModal } from '@/features/BookmarkCollections/components/ConfirmDeleteModal';
import { ShareCollectionModal } from '@/features/BookmarkCollections/components/ShareCollectionModal';

interface CollectionActionsDropdownProps {
  isOpen: boolean;
  onClose: () => void;
  onEdit: () => void;
  onDelete: () => void;
  onSelectBookmarks?: () => void;
  collection: BookmarkCollection | null;
  hasSharing?: boolean;
  hasSelection?: boolean;
}

export const CollectionActionsDropdown = ({
  isOpen,
  onEdit,
  onClose,
  onDelete,
  collection,
  onSelectBookmarks,
  hasSharing = false,
  hasSelection = true,
}: CollectionActionsDropdownProps) => {
  const { t } = useTranslation();

  const [isShareModalOpen, setIsShareModalOpen] = useState(false);
  const [isDestroyConfirmOpen, setIsDestroyConfirmOpen] = useState(false);

  const hasBookmarks = (collection?.ads?.totalCount ?? 0) > 0;

  const handleEditCollection = () => {
    onEdit();
    onClose();
  };

  const destroyCollection = () => {
    if (!collection) return;

    onDelete();
    setIsDestroyConfirmOpen(false);
  };

  const handleDeleteCollection = () => {
    onClose();
    setIsDestroyConfirmOpen(true);
  };

  const handleShareCollection = () => {
    setIsShareModalOpen(true);
    onClose();
  };

  const handleSelectBookmarks = () => {
    onSelectBookmarks?.();
    onClose();
  };

  return (
    <>
      <Dropdown isOpen={isOpen} onClose={onClose}>
        <S.Menu>
          {hasSelection && hasBookmarks && (
            <S.MenuItem onClick={handleSelectBookmarks}>
              <S.MenuItemText>
                {t('bookmark_collections.select_bookmarks_action')}
              </S.MenuItemText>
            </S.MenuItem>
          )}
          <S.MenuItem onClick={handleEditCollection}>
            <S.MenuItemText>
              {t('bookmark_collections.edit_collection_dropdown')}
            </S.MenuItemText>
          </S.MenuItem>
          <S.MenuItem onClick={handleDeleteCollection}>
            <S.MenuItemText $isDangerous>
              {t('bookmark_collections.delete_collection_dropdown')}
            </S.MenuItemText>
          </S.MenuItem>
          {hasSharing && (
            <S.MenuItem onClick={handleShareCollection}>
              <S.MenuItemText>
                {t('bookmark_collections.share_collection_dropdown')}
              </S.MenuItemText>
            </S.MenuItem>
          )}
        </S.Menu>
      </Dropdown>

      <Portal>
        <ConfirmDeleteModal
          onSubmit={destroyCollection}
          isOpen={isDestroyConfirmOpen}
          onClose={() => setIsDestroyConfirmOpen(false)}
        />
      </Portal>

      <Portal>
        <ShareCollectionModal
          isOpen={isShareModalOpen}
          onClose={() => setIsShareModalOpen(false)}
          link={`${window.location.origin}/collections/${collection?.id}`}
        />
      </Portal>
    </>
  );
};
